import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../../api/client";
import DispatchPanel from "../components/DispatchPanel";
import POPnlPanel from "../components/POPnlPanel";
import PermissionGate from "../../../components/common/PermissionGate";
import { PERMISSIONS } from "../../../constants/permissions";

const STATUS_BADGE = {
  pending: { bg: "#f1f5f9", color: "#475569", label: "Pending" },
  in_progress: { bg: "#eff6ff", color: "#1d4ed8", label: "In Progress" },
  completed: { bg: "#dcfce7", color: "#15803d", label: "Completed" },
  overdue: { bg: "#fee2e2", color: "#dc2626", label: "Overdue" },
  scheduled: { bg: "#eff6ff", color: "#1d4ed8", label: "Scheduled" },
  active: { bg: "#dcfce7", color: "#15803d", label: "Active" },
  at_risk: { bg: "#fff7ed", color: "#c2410c", label: "At Risk" },
};

const TYPE_COLOR = {
  po_created: "#1d4ed8",
  po_activated: "#15803d",
  po_completed: "#15803d",
  parts_reported: "#7c3aed",
  stage_started: "#0891b2",
  overdue_flagged: "#dc2626",
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || {
    bg: "#f1f5f9",
    color: "#475569",
    label: status,
  };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.color}33`,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {s.label}
    </span>
  );
}

function formatDateLong(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  const month = d.toLocaleString("en-GB", { month: "long" });
  const year = d.getFullYear();
  return `${day}${suffix} ${month}, ${year}`;
}

function formatTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "short" });
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${day} ${month}, ${h12}:${m}${ampm}`;
}

function ActionModal({ type, stage, allStages, onClose, onSuccess }) {
  const [qty, setQty] = useState("");
  const [targetId, setTargetId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isOk = type === "ok";
  // Parts run independent flows — a part can only be sent back within its own
  const earlier = allStages.filter(
    (s) =>
      s.stageOrder < stage.stageOrder &&
      (s.poPartId ?? null) === (stage.poPartId ?? null),
  );

  React.useEffect(() => {
    if (!isOk && earlier.length > 0) setTargetId(earlier[0].id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const n = Number(qty);
    if (!n || n <= 0) {
      setError("Enter a valid quantity");
      return;
    }
    if (n > stage.partsRemaining) {
      setError(`Cannot exceed remaining (${stage.partsRemaining})`);
      return;
    }
    if (!isOk && !targetId) {
      setError("Select a target stage");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (isOk) {
        await api.post(`/manufacturing/stages/${stage.id}/report`, {
          partsDoneToday: n,
        });
      } else {
        await api.post(`/manufacturing/stages/${stage.id}/${type}`, {
          quantity: n,
          targetStageProgressId: targetId,
        });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const titles = {
    ok: "Mark as OK",
    reject: "Reject Parts",
    rework: "Send for Rework",
  };
  const submitLabels = {
    ok: "Submit",
    reject: "Reject",
    rework: "Send for Rework",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 28,
          width: 380,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: "0 0 4px", color: "#0f2d5c", fontSize: 16 }}>
          {titles[type]}
        </h3>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 13 }}>
          Stage: <strong>{stage.stageName}</strong> — {stage.partsRemaining}{" "}
          parts remaining
        </p>
        <form onSubmit={handleSubmit}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 700,
              color: "#4f6280",
              marginBottom: 4,
            }}
          >
            Quantity *
          </label>
          <input
            type="number"
            min="1"
            max={stage.partsRemaining}
            placeholder={`Max ${stage.partsRemaining}`}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d6dfed",
              borderRadius: 8,
              fontSize: 14,
              boxSizing: "border-box",
              marginBottom: 12,
            }}
            autoFocus
            required
          />
          {!isOk && (
            <>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#4f6280",
                  marginBottom: 4,
                }}
              >
                Send back to *
              </label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d6dfed",
                  borderRadius: 8,
                  fontSize: 14,
                  boxSizing: "border-box",
                  marginBottom: 8,
                }}
                required
              >
                {earlier.length === 0 && (
                  <option value="">No earlier stages</option>
                )}
                {earlier.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.stageName}
                  </option>
                ))}
              </select>
            </>
          )}
          {error && (
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#dc2626" }}>
              {error}
            </p>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || (!isOk && earlier.length === 0)}
            >
              {submitting ? "Saving…" : submitLabels[type]}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CommentModal({ stage, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = React.useRef(null);

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/manufacturing/stages/${stage.id}/comments`);
      setComments(res.data.data || []);
    } catch {
      // silent
    }
  }, [stage.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      const res = await api.post(`/manufacturing/stages/${stage.id}/comments`, {
        message: text.trim(),
      });
      setComments((prev) => [...prev, res.data.data]);
      setText("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: 440,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px 12px",
            borderBottom: "1px solid #e4ebf5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f2d5c" }}>
              Comments
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {stage.stageName}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "#94a3b8",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Message list */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {comments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 13,
                marginTop: 32,
              }}
            >
              No comments yet. Be the first to add one.
            </div>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                style={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: "#0f2d5c" }}
                  >
                    {c.user.firstName} {c.user.lastName}
                  </span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {formatTs(c.createdAt)}
                  </span>
                </div>
                <div
                  style={{
                    background: "#f1f5f9",
                    borderRadius: "4px 12px 12px 12px",
                    padding: "8px 12px",
                    fontSize: 13,
                    color: "#334155",
                    lineHeight: 1.5,
                    maxWidth: "90%",
                  }}
                >
                  {c.message}
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <form
          onSubmit={handleSend}
          style={{ padding: "12px 20px 16px", borderTop: "1px solid #f1f5f9" }}
        >
          {error && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#dc2626" }}>
              {error}
            </p>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a comment…"
              style={{
                flex: 1,
                padding: "8px 12px",
                border: "1px solid #d6dfed",
                borderRadius: 8,
                fontSize: 14,
                outline: "none",
              }}
              disabled={sending}
              autoFocus
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={sending || !text.trim()}
              style={{ padding: "8px 16px", fontSize: 13 }}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function POCommentModal({ po, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = React.useRef(null);

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/manufacturing/pos/${po.id}/comments`);
      setComments(res.data.data || []);
    } catch {
      // silent
    }
  }, [po.id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      const res = await api.post(`/manufacturing/pos/${po.id}/comments`, {
        message: text.trim(),
      });
      setComments((prev) => [...prev, res.data.data]);
      setText("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: 440,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            padding: "16px 20px 12px",
            borderBottom: "1px solid #e4ebf5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f2d5c" }}>
              Message All
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {po.poNumber} — sent to all stage assignees
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "#94a3b8",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {comments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 13,
                marginTop: 32,
              }}
            >
              No messages yet. Send a message to all assignees.
            </div>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                style={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: "#0f2d5c" }}
                  >
                    {c.user.firstName} {c.user.lastName}
                  </span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {formatTs(c.createdAt)}
                  </span>
                </div>
                <div
                  style={{
                    background: "#f1f5f9",
                    borderRadius: "4px 12px 12px 12px",
                    padding: "8px 12px",
                    fontSize: 13,
                    color: "#334155",
                    lineHeight: 1.5,
                    maxWidth: "90%",
                  }}
                >
                  {c.message}
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={handleSend}
          style={{ padding: "12px 20px 16px", borderTop: "1px solid #f1f5f9" }}
        >
          {error && (
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#dc2626" }}>
              {error}
            </p>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message to all assignees…"
              style={{
                flex: 1,
                padding: "8px 12px",
                border: "1px solid #d6dfed",
                borderRadius: 8,
                fontSize: 14,
                outline: "none",
              }}
              disabled={sending}
              autoFocus
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={sending || !text.trim()}
              style={{ padding: "8px 16px", fontSize: 13 }}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [stages, setStages] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // { type: 'ok'|'reject'|'rework', stage }
  const [commentStage, setCommentStage] = useState(null);
  const [poCommentOpen, setPoCommentOpen] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, stagesRes] = await Promise.all([
        api.get(`/manufacturing/pos/${id}`),
        api.get(`/manufacturing/stages/po/${id}`),
      ]);
      setPo(poRes.data.data);
      setStages(stagesRes.data.data.stages || []);
      setActivityLogs(stagesRes.data.data.activityLogs || []);
    } catch {
      // handled by empty state
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleActionSuccess = () => {
    setActiveModal(null);
    load();
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await api.get(`/manufacturing/pos/${id}/report`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.poNumber}-report.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
        Loading…
      </div>
    );
  }

  if (!po) {
    return (
      <div style={{ padding: 20 }}>
        <Link className="back-link" to="/manufacturing/po-management">
          ← Back to PO Management
        </Link>
        <p style={{ color: "#dc2626", marginTop: 20 }}>PO not found.</p>
      </div>
    );
  }

  const partTypes = [...new Set((po.parts || []).map((p) => p.partType))].join(
    ", ",
  );
  const totalQty = (po.parts || []).reduce((sum, p) => sum + p.quantity, 0);
  const deadlineStr = po.deliveryDate ? po.deliveryDate.split("T")[0] : "";
  const isOverdue =
    deadlineStr && deadlineStr < today && po.status !== "completed";
  const partDeadlines = (po.parts || []).map((p) =>
    p.deliveryDate ? p.deliveryDate.split("T")[0] : "",
  );
  const hasMixedDeadlines = new Set(partDeadlines.filter(Boolean)).size > 1;

  // Each part runs its own workflow, so the stage table is grouped per part
  // rather than shown as one flat list of stage names.
  const stageGroups = (() => {
    const byPart = new Map();
    for (const s of stages) {
      const key = s.poPartId || "__unassigned__";
      if (!byPart.has(key)) byPart.set(key, []);
      byPart.get(key).push(s);
    }
    const keys = (po.parts || []).map((p) => p.id).filter((k) => byPart.has(k));
    for (const k of byPart.keys()) if (!keys.includes(k)) keys.push(k);
    return keys.map((key) => ({
      key,
      part: (po.parts || []).find((p) => p.id === key) || null,
      stages: byPart.get(key),
    }));
  })();

  return (
    <div
      style={{
        padding: 20,
        height: "100%",
        overflow: "auto",
        background: "#f5f8fc",
      }}
    >
      <Link className="back-link" to="/manufacturing/po-management">
        ← Back to PO Management
      </Link>

      <h1 style={{ margin: "12px 0 20px", color: "#0f2d5c" }}>{po.poNumber}</h1>

      {/* Summary bar */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e4ebf5",
          borderRadius: 12,
          padding: "16px 24px",
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: 28,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Part Types
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0f2d5c",
              marginTop: 2,
            }}
          >
            {partTypes || "—"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Total Qty
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0f2d5c",
              marginTop: 2,
            }}
          >
            {totalQty.toLocaleString()}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Customer
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0f2d5c",
              marginTop: 2,
            }}
          >
            {po.customerName}
          </div>
        </div>
        {po.location && (
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              Location
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f2d5c",
                marginTop: 2,
              }}
            >
              {po.location}
            </div>
          </div>
        )}
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Deadline
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: isOverdue ? "#dc2626" : "#0f2d5c",
              marginTop: 2,
            }}
          >
            {formatDateLong(deadlineStr)}
          </div>
          {hasMixedDeadlines && (
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
              Latest of {po.parts.length} part deadlines
            </div>
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              marginBottom: 4,
            }}
          >
            Status
          </div>
          <StatusBadge status={po.status} />
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            onClick={handleDownloadReport}
            disabled={downloadingReport}
            style={{
              padding: "8px 16px",
              background: "#fff",
              color: "#0f2d5c",
              border: "1px solid #d6dfed",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: downloadingReport ? "default" : "pointer",
            }}
          >
            {downloadingReport ? "Preparing…" : "Download Report"}
          </button>
          <button
            onClick={() => setPoCommentOpen(true)}
            style={{
              padding: "8px 16px",
              background: "#0f2d5c",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            💬 Message All
          </button>
        </div>
      </div>

      {/* Parts — each with its own delivery date */}
      {(po.parts || []).length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e4ebf5",
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 20,
          }}
        >
          <div
            style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}
          >
            <h2 style={{ margin: 0, fontSize: 16, color: "#0f2d5c" }}>Parts</h2>
          </div>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["Part", "Workflow", "Quantity", "Delivery Date", "Status"].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 16px",
                        textAlign: "left",
                        fontWeight: 700,
                        color: "#374151",
                        borderBottom: "1px solid #e4ebf5",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {po.parts.map((part, i) => {
                const partDeadline = partDeadlines[i];
                const partOverdue =
                  partDeadline &&
                  partDeadline < today &&
                  part.status !== "completed";
                return (
                  <tr
                    key={part.id}
                    style={{ borderBottom: "1px solid #f1f5f9" }}
                  >
                    <td
                      style={{
                        padding: "10px 16px",
                        fontWeight: 600,
                        color: "#0f2d5c",
                      }}
                    >
                      {part.partType}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>
                      {part.workflow?.name || "—"}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>
                      {part.quantity.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        color: partOverdue ? "#dc2626" : "#0f2d5c",
                        fontWeight: partOverdue ? 700 : 400,
                      }}
                    >
                      {partDeadline ? formatDateLong(partDeadline) : "—"}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <StatusBadge status={part.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Profit & Loss — prices and costs stay hidden without a P&L permission */}
      <PermissionGate
        permission={[PERMISSIONS.MANUFACTURING_PNL_VIEW, PERMISSIONS.MANUFACTURING_PNL_MANAGE]}
      >
        {/* A new reject or rework changes the reported counts */}
        <POPnlPanel poId={po.id} reloadKey={activityLogs.length} />
      </PermissionGate>

      {/* Stage Progress */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e4ebf5",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 20,
        }}
      >
        <div
          style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}
        >
          <h2 style={{ margin: 0, fontSize: 16, color: "#0f2d5c" }}>
            Stage Progress
          </h2>
        </div>

        {stages.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 14,
            }}
          >
            Stages will appear once the PO is activated.
          </div>
        ) : (
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {[
                  "Stage",
                  "Assigned To",
                  "Received",
                  "Completed",
                  "Remaining",
                  "Target Date",
                  "Status",
                  "Last Updated",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 16px",
                      textAlign: "left",
                      fontWeight: 700,
                      color: "#374151",
                      borderBottom: "1px solid #e4ebf5",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stageGroups.map((group) => (
                <React.Fragment key={group.key}>
                  {group.part && (
                    <tr style={{ background: "#f1f5f9" }}>
                      <td
                        colSpan={9}
                        style={{
                          padding: "8px 16px",
                          borderTop: "1px solid #e4ebf5",
                          borderBottom: "1px solid #e4ebf5",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#0f2d5c",
                          }}
                        >
                          {group.part.partType}
                        </span>
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          {" "}
                          — {group.part.workflow?.name || "No workflow"} ·{" "}
                          {group.part.quantity.toLocaleString()} qty · due{" "}
                          {group.part.deliveryDate
                            ? formatDateLong(
                                group.part.deliveryDate.split("T")[0],
                              )
                            : "—"}
                        </span>
                      </td>
                    </tr>
                  )}
                  {group.stages.map((stage) => {
                const isStageOverdue = stage.status === "overdue";
                const targetStr = stage.targetDate
                  ? stage.targetDate.split("T")[0]
                  : "";
                const targetOverdue =
                  targetStr &&
                  targetStr < today &&
                  stage.status !== "completed";
                const lastLog = stage.activityLogs?.[0];
                const isDispatch = stage.stage?.type === "dispatch";
                // Nothing to ship until parts actually arrive at the stage
                const showDispatch = isDispatch && stage.partsReceived > 0;
                const canReport =
                  !isDispatch &&
                  (stage.status === "in_progress" || stage.status === "overdue");

                return (
                  <React.Fragment key={stage.id}>
                  <tr
                    style={{
                      borderBottom: showDispatch ? "none" : "1px solid #f1f5f9",
                      background: isStageOverdue ? "#fff5f5" : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "10px 16px",
                        fontWeight: 600,
                        color: "#0f2d5c",
                      }}
                    >
                      {stage.stageName}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>
                      {stage.assignee
                        ? `${stage.assignee.firstName} ${stage.assignee.lastName}`
                        : "—"}
                    </td>
                    <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>
                      {stage.partsReceived.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        color: "#15803d",
                        fontWeight: 600,
                      }}
                    >
                      {stage.partsCompleted.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        color: stage.partsRemaining > 0 ? "#0f2d5c" : "#94a3b8",
                      }}
                    >
                      {stage.partsRemaining.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        color: targetOverdue ? "#dc2626" : "#0f2d5c",
                        fontWeight: targetOverdue ? 700 : 400,
                      }}
                    >
                      {formatDateLong(targetStr)}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <StatusBadge status={stage.status} />
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        color: "#64748b",
                        fontSize: 12,
                      }}
                    >
                      {lastLog ? formatTs(lastLog.createdAt) : "—"}
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <div
                        style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
                      >
                        {canReport && (
                          <>
                            <button
                              style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                background: "#dcfce7",
                                color: "#15803d",
                                border: "1px solid #86efac",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                setActiveModal({ type: "ok", stage })
                              }
                            >
                              ✓ OK
                            </button>
                            <button
                              style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                background: "#fee2e2",
                                color: "#dc2626",
                                border: "1px solid #fca5a5",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                setActiveModal({ type: "reject", stage })
                              }
                            >
                              ✕ Reject
                            </button>
                            <button
                              style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                background: "#fff7ed",
                                color: "#c2410c",
                                border: "1px solid #fdba74",
                                borderRadius: 6,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                setActiveModal({ type: "rework", stage })
                              }
                            >
                              ↺ Rework
                            </button>
                          </>
                        )}
                        <button
                          style={{
                            fontSize: 11,
                            padding: "3px 8px",
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                          onClick={() => setCommentStage(stage)}
                        >
                          💬 Comment
                        </button>
                      </div>
                    </td>
                  </tr>
                  {showDispatch && (
                    <tr
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isStageOverdue ? "#fff5f5" : "transparent",
                      }}
                    >
                      <td colSpan={9} style={{ padding: "0 16px 14px" }}>
                        <DispatchPanel
                          stageProgressId={stage.id}
                          shipments={stage.shipments}
                          partsReceived={stage.partsReceived}
                          onChanged={load}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Activity Log */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e4ebf5",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}
        >
          <h2 style={{ margin: 0, fontSize: 16, color: "#0f2d5c" }}>
            Activity Log
          </h2>
        </div>
        {activityLogs.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#94a3b8",
              fontSize: 14,
            }}
          >
            No activity recorded yet.
          </div>
        ) : (
          <div style={{ padding: "8px 24px 16px" }}>
            {activityLogs.map((log) => (
              <div
                key={log.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: "1px solid #f8fafc",
                }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    marginTop: 4,
                    background: TYPE_COLOR[log.type] || "#94a3b8",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: "#0f2d5c" }}>
                    {log.message}
                  </span>
                  {log.user && (
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      {" "}
                      — {log.user.firstName} {log.user.lastName}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#94a3b8",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatTs(log.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeModal && (
        <ActionModal
          type={activeModal.type}
          stage={activeModal.stage}
          allStages={stages}
          onClose={() => setActiveModal(null)}
          onSuccess={handleActionSuccess}
        />
      )}

      {commentStage && (
        <CommentModal
          stage={commentStage}
          onClose={() => setCommentStage(null)}
        />
      )}

      {poCommentOpen && (
        <POCommentModal po={po} onClose={() => setPoCommentOpen(false)} />
      )}
    </div>
  );
}
